import { useEffect, useState } from "react";
import { apiRequest } from "@/lib/api";

type PackageCode = "free" | "plus" | "premium";

const LABELS: Record<PackageCode, string> = {
  free: "Ücretsiz",
  plus: "Plus",
  premium: "Premium",
};

interface PackageState {
  product_code: PackageCode;
  status: string;
  expires_at: string | null;
  device_limit: number | null;
}

export default function StudentPackageControl({ studentId }: { studentId: string }) {
  const [current, setCurrent] = useState<PackageState | null>(null);
  const [selected, setSelected] = useState<PackageCode>("free");
  const [cycle,setCycle]=useState("monthly");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiRequest<{ package: PackageState }>(`/admin/users/${encodeURIComponent(studentId)}/package`)
      .then(({ package: data }) => {
        if (!active) return;
        setCurrent(data);
        setSelected(data.product_code in LABELS ? data.product_code : "free");
      })
      .catch((error) => active && setMessage(error instanceof Error ? error.message : "Paket bilgisi yüklenemedi."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [studentId]);

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      const result = await apiRequest<{ package: PackageState }>(`/admin/users/${encodeURIComponent(studentId)}/package`, {
        method: "PUT",
        body: { product_code: selected, billing_cycle:cycle },
      });
      setCurrent(result.package);
      setMessage(`${LABELS[selected]} paketi atandı.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Paket güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };

  const expiry = current?.expires_at
    ? new Date(current.expires_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
    : "Süresiz";

  return (
    <section className="rounded-lg border border-primary-100 bg-primary-50/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground-900">Öğrenci Paketi</h3>
          <p className="mt-1 text-xs text-foreground-600">Meraklı ve Dersdinle erişim düzeyini yönet.</p>
        </div>
        {current && <span className="rounded-full bg-background-50 px-2.5 py-1 text-xs font-semibold text-primary-700">{LABELS[current.product_code] ?? current.product_code}</span>}
      </div>
      {loading ? (
        <p className="mt-3 text-sm text-foreground-500">Paket bilgisi yükleniyor...</p>
      ) : (
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="flex-1 text-xs font-medium text-foreground-700">
            Paket
            <select value={selected} onChange={(event) => setSelected(event.target.value as PackageCode)} className="mt-1.5 h-10 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400">
              <option value="free">Ücretsiz</option>
              <option value="plus">Plus</option>
              <option value="premium">Premium</option>
            </select>
          </label>
          <label className="text-xs">Süre<select aria-label="Paket süresi" value={cycle} onChange={e=>setCycle(e.target.value)} disabled={selected==="free"} className="block h-10 rounded-md border px-3"><option value="monthly">1 ay</option><option value="yearly">12 ay</option></select></label>
          <button type="button" onClick={save} disabled={saving} className="h-10 rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60">
            {saving ? "Güncelleniyor..." : "Paketi Güncelle"}
          </button>
        </div>
      )}
      {!loading && current && <p className="mt-2 text-xs text-foreground-600">Durum: {current.status === "active" ? "Aktif" : current.status} · Bitiş: {expiry} · Sınırsız cihaz</p>}
      {message && <p className="mt-2 text-xs text-foreground-700" role="status">{message}</p>}
    </section>
  );
}
